import React, { FC, Fragment, useCallback, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { User, UserListFilter } from 'models/user';
import { useQuery, NetworkStatus, useLazyQuery } from '@apollo/client';
import { getDentistsForUserListTemplate } from 'apollo/templates/user/query.graphql';
import { labClientTemplate } from 'apollo/templates/client-directory';
import DownloadExportButton from 'components/shared/download-export-button/download-export-button.component';
import BackdropLoader from 'components/shared/backdrop-loader/backdrop-loader.component';
import { useDentistListExport } from 'hooks/useDentistListExport';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { debounce } from 'lodash';
import Loader from 'components/shared/loader/loader.component';
import DentistPageActions from 'components/users/dentists/dentist-page-actions/dentist-page-actions.component';
import ClientDirectoryDentistList from './dentist-list/client-directory-dentist-list.component';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import { LabClient } from 'models/client-directory';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import LabAddDentistButton from 'components/users/shared/lab-add-dentist-button/lab-add-dentist-button.component';

const ClientDirectoryDentists: FC = () => {
    const { t: translate } = useTranslation(['users', 'common']);
    const { id } = useParams<{ id: string }>();
    const [lastOffset, setLastOffset] = useState(0);
    const [filter, setFilter] = useState<UserListFilter>({ freeText: '', active: true, role: '' });
    const [dentists, setDentists] = useState<User[]>([]);
    const [snackbarOpen, setSnackbarOpen] = useState({ addDentist: false, updateDentist: false });
    const [labClient, setLabClient] = useState<LabClient>({} as LabClient);
    const [showAddConnectedDentistButton, setShowAddConnectedDentistButton] = useState<boolean>(false);
    const { showBoundary } = useDenthubErrorBoundary();

    const [getLabClient, { loading: labClientLoading, refetch: refetchLabClient }] = useLazyQuery(labClientTemplate, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only',
        onCompleted: ({ labClient }) => {
            setLabClient(labClient);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        getLabClient({
            variables: {
                clientId: id,
            },
        });
    }, []);

    useEffect(() => {
        if (labClient.customer) {
            let showAddConnectedDentistButton = customerSettings.getCustomerSetting(
                labClient.customer.customerSettings || [],
                CONSTANTS.SETTINGS_NAMES.ADD_CONNECTED_DENTIST,
            );
            // If the clinic is a child clinic, check the parent clinic for the customer setting
            if (labClient.customer.parentClinicId) {
                showAddConnectedDentistButton = customerSettings.getCustomerSetting(
                    labClient.customer.parentClinic?.customerSettings || [],
                    CONSTANTS.SETTINGS_NAMES.ADD_CONNECTED_DENTIST,
                );
            }
            setShowAddConnectedDentistButton(showAddConnectedDentistButton);
        }
    }, [labClient]);

    const mainClinicId = labClient.customer?.parentClinicId || labClient.customer?._id;
    // A default clinic only exists in a parent-child organization: the customer is either
    // a main clinic with children or a child clinic itself.
    const showDefaultClinicColumn = !!labClient.customer?.isMain || !!labClient.customer?.parentClinicId;

    const {
        loading: dentistsLoading,
        fetchMore,
        networkStatus,
    } = useQuery(getDentistsForUserListTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: {
            clinicId: id,
            filters: filter,
            limit: 25,
            offset: 0,
        },
        onCompleted: ({ dentists }) => {
            // TODO: This component has some strange issues and this is called with undefined data after adding dentists.
            setDentists(dentists);
            if (dentists) {
                setLastOffset(dentists.length);
            } else {
                setLastOffset(0);
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleFetchMore = () => {
        fetchMore({
            variables: {
                offset: lastOffset,
            },
        });
    };

    // Whole customer population regardless of the list filters; the backend builds the
    // workbook in the background and the hook downloads it when the socket event arrives.
    const { requestExport: exportDentistList, isExporting } = useDentistListExport({ clinicId: id });

    const handleFilterChange = useCallback(
        debounce(({ freeText, active, role }: UserListFilter) => {
            setLastOffset(0);
            setFilter({ freeText: freeText.toLocaleLowerCase(), active: active, role: role });
        }, 800),
        [],
    );

    const onDentistCreation = () => {
        setSnackbarOpen({ ...snackbarOpen, addDentist: true });
        reFetchClient();
    };

    const onDentistUpdate = async (dentist: User) => {
        const dentistsArr = [...dentists];
        const findIndex = dentistsArr.findIndex((d) => d._id === dentist._id);
        const dentistObj = Object.assign({ ...dentistsArr[findIndex], ...dentist });
        dentistsArr.splice(findIndex, 1, dentistObj);
        setDentists(dentistsArr);
        setSnackbarOpen({ ...snackbarOpen, updateDentist: true });
        reFetchClient();
    };

    const reFetchClient = () => {
        if (refetchLabClient) {
            refetchLabClient();
        }
    };

    const handleUserLinked = (user: User) => {
        setDentists([...dentists, user]);
        setSnackbarOpen({ ...snackbarOpen, addDentist: true });
    };

    return (
        <Fragment>
            <Typography mb={1} variant="h2">
                {translate('common:DENTISTS')}
            </Typography>
            {labClient?._id ? (
                <Fragment>
                    <Box display="flex">
                        <DentistPageActions onFilterChange={handleFilterChange} />
                        {/* LabAddDentistButton offsets itself with mt={1} to line up with the filter
                            inputs; the export button gets the same offset so the two sit on one line. */}
                        <Box ml="auto" display="flex" alignItems="flex-start" gap={1}>
                            <Box mt={1}>
                                <DownloadExportButton
                                    label={translate('users:EXPORT_DENTISTS_EXCEL')}
                                    isLoading={isExporting}
                                    onDownload={exportDentistList}
                                />
                            </Box>
                            <LabAddDentistButton
                                clinicId={mainClinicId}
                                isWhiteLabel={labClient.customer?.isWhiteLabeled || false}
                                showAddConnectedDentistButton={showAddConnectedDentistButton}
                                labClientCustomerSettings={labClient.customer?.customerSettings}
                                onDentistCreation={onDentistCreation}
                                reFetchClient={reFetchClient}
                                onUserLinked={handleUserLinked}
                                variant="contained"
                            />
                        </Box>
                    </Box>
                    {(dentistsLoading || labClientLoading) && networkStatus !== NetworkStatus.fetchMore ? (
                        <Loader />
                    ) : (
                        <Fragment>
                            <ClientDirectoryDentistList
                                isWhiteLabel={labClient.customer?.isWhiteLabeled}
                                showDefaultClinicColumn={showDefaultClinicColumn}
                                filteredDentists={dentists}
                                clientId={mainClinicId}
                                onFetchMore={handleFetchMore}
                                onUserUpdated={onDentistUpdate}
                            />
                            {snackbarOpen.addDentist && (
                                <AlertSnackbar type="success" message={translate('users:DENTIST_ADDED')} />
                            )}
                            {snackbarOpen.updateDentist && (
                                <AlertSnackbar type="success" message={translate('users:DENTIST_UPDATED')} />
                            )}
                        </Fragment>
                    )}
                    <BackdropLoader isOpen={isExporting} message={translate('users:CREATING_EXPORT_WAIT')} />
                </Fragment>
            ) : undefined}
        </Fragment>
    );
};

export default ClientDirectoryDentists;
